/**
 * Photo storage buckets.
 *
 * Every photo file path has the shape
 * `{organisation_id}/{subject_folder}/{subject_id}/{photo_id}.{ext}`.
 *
 * Survey site and observation photos share the `photos` bucket; geospatial
 * record photos have their own. The subject folder is what tells them apart,
 * so the bucket is derivable from the stored path alone — no extra lookup and
 * nothing extra to persist on the photo row.
 *
 * Keep in sync with `bucketForPath` in the `delete_photo_storage` edge
 * function, which resolves the same thing server-side.
 */

export const PHOTOS_BUCKET = "photos"
export const GEOSPATIAL_PHOTOS_BUCKET = "geospatial-photos"

/** The subject folder segment used in a photo's storage path. */
export type PhotoSubjectFolder =
  | "survey_sites"
  | "observations"
  | "geospatial_data"

export const GEOSPATIAL_SUBJECT_FOLDER: PhotoSubjectFolder = "geospatial_data"

/**
 * Resolve the storage bucket a photo file path belongs to.
 *
 * Falls back to the `photos` bucket for unrecognised paths: those are
 * survey-site and observation photos written before this distinction existed,
 * and they all live there.
 */
export function bucketForPhotoPath(
  filePath: string | null | undefined,
): string {
  if (!filePath) return PHOTOS_BUCKET
  return filePath.split("/")[1] === GEOSPATIAL_SUBJECT_FOLDER
    ? GEOSPATIAL_PHOTOS_BUCKET
    : PHOTOS_BUCKET
}

/**
 * Group file paths by the bucket that holds them, preserving each path's
 * index in the original array so callers that zip results back against a
 * parallel list (photo records, say) can restore the original order.
 */
export function groupPhotoPathsByBucket(
  filePaths: string[],
): Map<string, Array<{ path: string; index: number }>> {
  const groups = new Map<string, Array<{ path: string; index: number }>>()
  filePaths.forEach((path, index) => {
    const bucket = bucketForPhotoPath(path)
    const group = groups.get(bucket)
    if (group) {
      group.push({ path, index })
    } else {
      groups.set(bucket, [{ path, index }])
    }
  })
  return groups
}
